// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	GuildEmoji,
	GuildEmojiCategoryResponse,
	GuildEmojiTagResponse,
	GuildEmojiWithUser,
	GuildSticker,
	GuildStickerWithUser,
} from '@fluxer/schema/src/domains/guild/GuildEmojiSchemas';
import type {UserPartial} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {sortBySnowflakeDesc} from '@fluxer/snowflake/src/SnowflakeUtils';

type EmojiUpdateListener = (emojis: ReadonlyArray<GuildEmojiWithUser>) => void;
type StickerUpdateListener = (stickers: ReadonlyArray<GuildStickerWithUser>) => void;
type EmojiCategoryUpdateListener = (categories: ReadonlyArray<GuildEmojiCategoryResponse>) => void;
type EmojiTagUpdateListener = (tags: ReadonlyArray<GuildEmojiTagResponse>) => void;

const MAX_GUILD_EXPRESSION_TAB_CACHE_ENTRIES = 50;

const emojiCache = new Map<string, ReadonlyArray<GuildEmojiWithUser>>();
const stickerCache = new Map<string, ReadonlyArray<GuildStickerWithUser>>();
const emojiCategoryCache = new Map<string, ReadonlyArray<GuildEmojiCategoryResponse>>();
const emojiTagCache = new Map<string, ReadonlyArray<GuildEmojiTagResponse>>();
const emojiAccessSequence = new Set<string>();
const stickerAccessSequence = new Set<string>();
const emojiCategoryAccessSequence = new Set<string>();
const emojiTagAccessSequence = new Set<string>();
const emojiListeners = new Map<string, Set<EmojiUpdateListener>>();
const stickerListeners = new Map<string, Set<StickerUpdateListener>>();
const emojiCategoryListeners = new Map<string, Set<EmojiCategoryUpdateListener>>();
const emojiTagListeners = new Map<string, Set<EmojiTagUpdateListener>>();

function freezeList<T>(items: ReadonlyArray<T>): ReadonlyArray<T> {
	return Object.freeze([...items]);
}

function notifyListeners<T>(
	listeners: Map<string, Set<(items: ReadonlyArray<T>) => void>>,
	guildId: string,
	value: ReadonlyArray<T>,
) {
	const listenersForGuild = listeners.get(guildId);
	if (!listenersForGuild) return;
	for (const listener of listenersForGuild) {
		listener(value);
	}
}

function setCache<
	T extends {
		id: string;
	},
>(
	cache: Map<string, ReadonlyArray<T>>,
	accessSequence: Set<string>,
	listeners: Map<string, Set<(items: ReadonlyArray<T>) => void>>,
	guildId: string,
	value: ReadonlyArray<T>,
	shouldNotify: boolean,
) {
	const frozen = freezeList(sortBySnowflakeDesc(value));
	cache.set(guildId, frozen);
	accessSequence.delete(guildId);
	accessSequence.add(guildId);
	evictCacheIfNeeded(cache, accessSequence);
	if (shouldNotify) {
		notifyListeners(listeners, guildId, frozen);
	}
}

function evictCacheIfNeeded<T>(cache: Map<string, T>, accessSequence: Set<string>) {
	for (const guildId of accessSequence) {
		if (!cache.has(guildId)) {
			accessSequence.delete(guildId);
		}
	}
	while (cache.size > MAX_GUILD_EXPRESSION_TAB_CACHE_ENTRIES) {
		const guildId = accessSequence.values().next().value;
		if (guildId == null) {
			break;
		}
		cache.delete(guildId);
		accessSequence.delete(guildId);
	}
}

function setEmojiCategoryCache(
	guildId: string,
	categories: ReadonlyArray<GuildEmojiCategoryResponse>,
	shouldNotify: boolean,
): void {
	const frozen = Object.freeze([...categories]);
	emojiCategoryCache.set(guildId, frozen);
	emojiCategoryAccessSequence.delete(guildId);
	emojiCategoryAccessSequence.add(guildId);
	evictCacheIfNeeded(emojiCategoryCache, emojiCategoryAccessSequence);
	if (shouldNotify) {
		notifyListeners(emojiCategoryListeners, guildId, frozen);
	}
}

function setEmojiTagCache(guildId: string, tags: ReadonlyArray<GuildEmojiTagResponse>, shouldNotify: boolean): void {
	const frozen = Object.freeze([...tags]);
	emojiTagCache.set(guildId, frozen);
	emojiTagAccessSequence.delete(guildId);
	emojiTagAccessSequence.add(guildId);
	evictCacheIfNeeded(emojiTagCache, emojiTagAccessSequence);
	if (shouldNotify) {
		notifyListeners(emojiTagListeners, guildId, frozen);
	}
}

export function seedGuildEmojiCache(guildId: string, emojis: ReadonlyArray<GuildEmojiWithUser>): void {
	setCache(emojiCache, emojiAccessSequence, emojiListeners, guildId, emojis, false);
}

export function seedGuildEmojiCategoryCache(
	guildId: string,
	categories: ReadonlyArray<GuildEmojiCategoryResponse>,
): void {
	setEmojiCategoryCache(guildId, categories, false);
}

export function seedGuildEmojiTagCache(guildId: string, tags: ReadonlyArray<GuildEmojiTagResponse>): void {
	setEmojiTagCache(guildId, tags, false);
}

export function seedGuildStickerCache(guildId: string, stickers: ReadonlyArray<GuildStickerWithUser>): void {
	setCache(stickerCache, stickerAccessSequence, stickerListeners, guildId, stickers, false);
}

function subscribe<T>(
	listeners: Map<string, Set<(items: ReadonlyArray<T>) => void>>,
	guildId: string,
	listener: (items: ReadonlyArray<T>) => void,
): () => void {
	let listenersForGuild = listeners.get(guildId);
	if (!listenersForGuild) {
		listenersForGuild = new Set();
		listeners.set(guildId, listenersForGuild);
	}
	listenersForGuild.add(listener);
	return () => {
		listenersForGuild.delete(listener);
		if (listenersForGuild.size === 0) listeners.delete(guildId);
	};
}

function patchFromGateway<T extends {id: string; user?: UserPartial}>(
	cache: Map<string, ReadonlyArray<T & {user: UserPartial}>>,
	accessSequence: Set<string>,
	listeners: Map<string, Set<(items: ReadonlyArray<T & {user: UserPartial}>) => void>>,
	guildId: string,
	updates: ReadonlyArray<T>,
): void {
	const previous = cache.get(guildId) ?? [];
	const previousUserById = new Map(previous.map((item) => [item.id, item.user]));
	const next = updates
		.map((item) => {
			const user = item.user ?? previousUserById.get(item.id);
			return user ? {...item, user} : null;
		})
		.filter((entry): entry is T & {user: UserPartial} => Boolean(entry));
	setCache(cache, accessSequence, listeners, guildId, next, true);
}

export function subscribeToGuildEmojiUpdates(guildId: string, listener: EmojiUpdateListener): () => void {
	return subscribe(emojiListeners, guildId, listener);
}

export function subscribeToGuildEmojiCategoryUpdates(
	guildId: string,
	listener: EmojiCategoryUpdateListener,
): () => void {
	let listenersForGuild = emojiCategoryListeners.get(guildId);
	if (!listenersForGuild) {
		listenersForGuild = new Set();
		emojiCategoryListeners.set(guildId, listenersForGuild);
	}
	listenersForGuild.add(listener);
	return () => {
		listenersForGuild?.delete(listener);
		if (listenersForGuild && listenersForGuild.size === 0) {
			emojiCategoryListeners.delete(guildId);
		}
	};
}

export function subscribeToGuildEmojiTagUpdates(guildId: string, listener: EmojiTagUpdateListener): () => void {
	let listenersForGuild = emojiTagListeners.get(guildId);
	if (!listenersForGuild) {
		listenersForGuild = new Set();
		emojiTagListeners.set(guildId, listenersForGuild);
	}
	listenersForGuild.add(listener);
	return () => {
		listenersForGuild?.delete(listener);
		if (listenersForGuild && listenersForGuild.size === 0) {
			emojiTagListeners.delete(guildId);
		}
	};
}

export function subscribeToGuildStickerUpdates(guildId: string, listener: StickerUpdateListener): () => void {
	return subscribe(stickerListeners, guildId, listener);
}

export function patchGuildEmojiCacheFromGateway(
	guildId: string,
	updates: ReadonlyArray<GuildEmoji>,
	categories?: ReadonlyArray<GuildEmojiCategoryResponse>,
	tags?: ReadonlyArray<GuildEmojiTagResponse>,
) {
	const previous = emojiCache.get(guildId) ?? [];
	const previousUserById = new Map(previous.map((emoji) => [emoji.id, emoji.user]));
	const next = updates
		.map((emoji) => {
			const user = emoji.user ?? previousUserById.get(emoji.id);
			if (!user) {
				return null;
			}
			return {
				...emoji,
				user,
			};
		})
		.filter((entry): entry is GuildEmojiWithUser => Boolean(entry));
	setCache(emojiCache, emojiAccessSequence, emojiListeners, guildId, next, true);
	if (categories !== undefined) {
		setEmojiCategoryCache(guildId, categories, true);
	}
	if (tags !== undefined) {
		setEmojiTagCache(guildId, tags, true);
	}
}
export function patchGuildStickerCacheFromGateway(guildId: string, updates: ReadonlyArray<GuildSticker>): void {
	patchFromGateway(stickerCache, stickerAccessSequence, stickerListeners, guildId, updates);
}
